import { DXAReading } from '@dynamika-shared/types/DXAReading';
import { CellValuesCortege } from './CellValuesCortege';
import type { TableProps } from 'antd';

import { shortDate, timeSec } from '@dynamika-shared/utils/date';
import { twJoin } from 'tailwind-merge';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { twMerge } from '@dynamika-shared/utils/twMerge';
import { ContextData } from '@dynamika-shared/types/clinicalTrialContext';

type Lab = ContextData['configuration']['labelGroups'][0]['labels'][0];

export type Label = {
  id: string;
  name: string;
  dataType: Lab['dataType'];
  modalityName: string;
};

type MakeType = {
  onSelect?: (readingId: string, labelId: string) => void;
  onRemove?: (reading: DXAReading) => void;
  isWorkstation?: boolean;
  allLabels?: Label[];
  availableLabels?: Label[];
  selected?: { [key: string]: string };
  readOnly?: boolean;
};

export function makeColumns(
  props: MakeType
): TableProps<DXAReading>['columns'] {
  const options = props.availableLabels?.map(label => ({
    label: label.name,
    value: label.id,
  }));
  return [
    {
      key: 'acquisitionDate',
      dataIndex: 'acquisitionDate',
      title() {
        return 'Acquisition Date';
      },
      hidden: props.isWorkstation,
      render(text, row) {
        if (!row.acquisitionDate) {
          return <div className='flex items-center h-full'>-</div>;
        }
        return (
          <div
            className={twJoin(
              'flex justify-center flex-col h-full align-middle text-1624 mini:text-1216 font-medium'
            )}
          >
            <div className='text-1624 mini:text-1216 font-semibold'>
              {shortDate(row.acquisitionDate)}
            </div>
            <div className='text-1216 text-spanishGray'>
              {timeSec(row.acquisitionDate)}
            </div>
          </div>
        );
      },
    },
    {
      key: 'region',
      dataIndex: 'region',
      title() {
        return 'Region';
      },
      render(text, row) {
        if (!row.bodyRegion) {
          return <div className='flex items-center h-full'>-</div>;
        }
        return (
          <div className='flex items-center h-full align-middle text-1624 mini:text-1216 font-medium'>
            {row.bodyRegion}
          </div>
        );
      },
    },
    {
      key: 'laterality',
      dataIndex: 'laterality',
      title() {
        return 'Laterality/Vertebra';
      },

      render(text, row) {
        if (!row.bodyProperty) {
          return <div className='flex items-center h-full'>-</div>;
        }
        return (
          <div className='flex items-center h-full text-1624 mini:text-1216 font-medium'>
            {row.bodyProperty}
          </div>
        );
      },
    },
    {
      width: 260,
      key: 'densitometry',
      dataIndex: 'densitometry',
      title() {
        return 'Densitometry (BMD/BMC/Area)';
      },

      render(text, row) {
        if (!row.densitometryScore) {
          return <div className='flex items-center h-full'>-</div>;
        }
        return (
          <CellValuesCortege
            cortege={[
              ['BMD', row.densitometryScore.bmd],
              ['BMC', row.densitometryScore.bmc],
              ['ARE', row.densitometryScore.area],
            ]}
          />
        );
      },
    },
    {
      width: 260,
      key: 'composition',
      dataIndex: 'composition',
      title() {
        return 'Composition (Bone/Fat/Lean)';
      },

      render(text, row) {
        if (!row.compositionScore) {
          return <div className='flex items-center h-full'>-</div>;
        }
        return (
          <CellValuesCortege
            cortege={[
              ['Bone', row?.compositionScore?.boneMass],
              ['Fat', row?.compositionScore?.fatMass],
              ['Lean', row?.compositionScore?.leanMass],
            ]}
          />
        );
      },
    },
    {
      key: 'tScore',
      dataIndex: 'tScore',
      title() {
        return 'T-Score';
      },

      render(text, row) {
        if (!row.tScore) {
          return <div className='flex items-center h-full'>-</div>;
        }
        return (
          <div className='flex items-center h-full text-1624 mini:text-1216 font-medium'>
            {row.tScore}
          </div>
        );
      },
    },
    {
      key: 'labelName',
      dataIndex: 'labelName',
      title() {
        return 'Assigned Label';
      },

      render(text, row) {
        if (!row.labelName) {
          return <div className='flex items-center h-full'>-</div>;
        }
        return (
          <div className='flex items-center h-full text-1624 mini:text-1216 font-medium'>
            {row?.labelName}
          </div>
        );
      },
    },
    {
      hidden: props.isWorkstation,
      key: 'action',
      dataIndex: 'action',
      title() {
        return 'Select for Reading';
      },
      render(text, row) {
        return (
          <div className='flex items-center h-full justify-end text-spanishGray font-semibold'>
            <button
              onClick={() => props?.onRemove?.(row)}
              className={twMerge(
                'text-vividTangelo hover:text-dodgerBlue',
                props.readOnly &&
                  'text-spanishGray hover:text-spanishGray hover:cursor-pointer'
              )}
              disabled={props.readOnly}
            >
              Remove
            </button>
          </div>
        );
      },
      align: 'right',
    },
    {
      hidden: !props.isWorkstation,
      key: 'AddToReading',
      dataIndex: 'AddToReading',
      title() {
        return 'Assign Label';
      },
      width: 200,
      render(text, row) {
        const selectedLabelId = props.selected?.[row.recordId ?? ''];
        const value = props.allLabels?.find(l => l.id === selectedLabelId);
        return (
          <div className='mt-1.5 text-start'>
            <Select
              allowClear
              placeholder='Assign Label'
              className='w-full !h-[4rem]'
              value={
                selectedLabelId
                  ? {
                      label: value?.name,
                      value: selectedLabelId,
                    }
                  : undefined
              }
              onChange={v => {
                props.onSelect?.(row.recordId ?? '', v ?? selectedLabelId);
              }}
              options={options}
              disabled={props.readOnly}
            />
          </div>
        );
      },
      align: 'right',
    },
  ];
}
